import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEO, { organizationSchema, createFAQSchema, softwareApplicationSchema } from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Check, Star, Users, Zap, Camera, Sparkles, Clock, Shield } from "lucide-react";
import { Link } from "react-router-dom";

const AIHeadshotGenerator = () => {
  const faqs = [
    {
      question: "How does the AI headshot generator work?",
      answer: "Our AI headshot generator uses advanced FLUX models to create professional-quality headshots. Simply describe what you want - professional business headshot, LinkedIn photo, creative portrait - and the AI generates multiple options instantly."
    },
    {
      question: "Are AI-generated headshots suitable for LinkedIn?",
      answer: "Yes! Our AI headshots are designed to meet LinkedIn's professional standards. Many users successfully use our AI-generated headshots for their LinkedIn profiles, resumes, and business cards."
    },
    {
      question: "How many headshots can I generate?",
      answer: "Free users can generate 5 images per day. Our Pro plan offers 500+ headshots monthly, perfect for agencies creating headshots for multiple clients or testing different styles."
    },
    {
      question: "Can I customize the background and style?",
      answer: "Absolutely! You can specify backgrounds (solid colors, office settings, outdoor), lighting styles, clothing, and more in your prompt. Our AI adapts to create exactly what you envision."
    },
    {
      question: "Are the headshots commercially usable?",
      answer: "Yes, all images generated on paid plans come with full commercial rights. You can use them for professional profiles, marketing materials, and business purposes."
    }
  ];

  const features = [
    {
      icon: <Camera className="w-6 h-6" />,
      title: "Professional Quality",
      description: "Studio-quality headshots without the studio. Perfect lighting, composition, and resolution."
    },
    {
      icon: <Zap className="w-6 h-6" />,
      title: "Instant Results",
      description: "Generate professional headshots in seconds, not days. No appointments needed."
    },
    {
      icon: <Sparkles className="w-6 h-6" />,
      title: "Multiple Styles",
      description: "Business formal, creative, casual - generate variations to find your perfect look."
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Commercial Rights",
      description: "Full commercial usage rights on all paid plans. Use anywhere, anytime."
    }
  ];

  const useCases = [
    "LinkedIn Profile Photos",
    "Resume & CV Photos",
    "Corporate Directories",
    "Speaker & Author Bios",
    "Real Estate Agent Photos",
    "Team Pages",
    "Business Cards",
    "Email Signatures"
  ];

  return (
    <div className="min-h-screen w-full overflow-x-hidden">
      <SEO
        title="AI Headshot Generator | Professional Business Photos in Seconds"
        description="Generate professional AI headshots for LinkedIn, resumes & business. No photographer needed. Studio-quality results in seconds. Free trial - create your perfect headshot now!"
        keywords="ai headshot generator, professional headshot ai, linkedin photo generator, business headshot maker, ai portrait generator, corporate headshot ai, free ai headshots, professional photo ai"
        url="/ai-headshot-generator"
        jsonLd={[
          organizationSchema,
          softwareApplicationSchema,
          createFAQSchema(faqs)
        ]}
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'AI Headshot Generator', url: '/ai-headshot-generator' }
        ]}
      />
      
      <Header />
      
      <main className="w-full">
        {/* Hero Section */}
        <section className="relative py-20 lg:py-32 bg-gradient-to-br from-background via-background to-primary/5">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Star className="w-4 h-4 fill-current" />
                90,000+ Headshots Generated Monthly
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 speakable-title">
                AI Headshot Generator
                <span className="block text-primary mt-2">Professional Photos in Seconds</span>
              </h1>
              
              <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto speakable-description">
                Skip the photographer. Generate stunning professional headshots with AI. 
                Perfect for LinkedIn, resumes, and business use. Studio quality, instant delivery.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
                <Button asChild size="lg" className="text-lg px-8">
                  <Link to="/try-generator">Generate Free Headshot</Link>
                </Button>
                <Button asChild variant="outline" size="lg" className="text-lg px-8">
                  <Link to="/gallery">View Examples</Link>
                </Button>
              </div>
              
              <div className="flex items-center justify-center gap-6 text-sm text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Check className="w-4 h-4 text-green-500" /> No signup required
                </span>
                <span className="flex items-center gap-1">
                  <Check className="w-4 h-4 text-green-500" /> 5 free daily
                </span>
                <span className="flex items-center gap-1">
                  <Check className="w-4 h-4 text-green-500" /> Commercial rights
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* Features Grid */}
        <section className="py-20 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Why Choose Our AI Headshot Generator?</h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                Professional headshots without the hassle. Perfect for individuals and teams.
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
              {features.map((feature, index) => (
                <div key={index} className="bg-card p-6 rounded-xl border shadow-sm hover:shadow-md transition-shadow">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center text-primary mb-4">
                    {feature.icon}
                  </div>
                  <h3 className="font-semibold text-lg mb-2">{feature.title}</h3>
                  <p className="text-muted-foreground text-sm">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Use Cases */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold mb-4">Perfect For Every Professional Need</h2>
                <p className="text-muted-foreground">
                  AI headshots trusted by professionals across industries
                </p>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {useCases.map((useCase, index) => (
                  <div key={index} className="flex items-center gap-2 bg-muted/50 p-4 rounded-lg">
                    <Check className="w-5 h-5 text-primary flex-shrink-0" />
                    <span className="text-sm font-medium">{useCase}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-16 bg-primary text-primary-foreground">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto text-center">
              <div>
                <div className="text-4xl font-bold mb-2">90K+</div>
                <div className="text-primary-foreground/80">Headshots Generated</div>
              </div>
              <div>
                <div className="text-4xl font-bold mb-2">&lt;10s</div>
                <div className="text-primary-foreground/80">Average Generation Time</div>
              </div>
              <div>
                <div className="text-4xl font-bold mb-2">4.9/5</div>
                <div className="text-primary-foreground/80">User Rating</div>
              </div>
              <div>
                <div className="text-4xl font-bold mb-2">100%</div>
                <div className="text-primary-foreground/80">Commercial Rights</div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-12">Frequently Asked Questions</h2>
              
              <div className="space-y-6">
                {faqs.map((faq, index) => (
                  <div key={index} className="bg-card border rounded-lg p-6">
                    <h3 className="font-semibold text-lg mb-2">{faq.question}</h3>
                    <p className="text-muted-foreground">{faq.answer}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-muted/30">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold mb-4">Ready for Your Perfect Headshot?</h2>
            <p className="text-muted-foreground mb-8 max-w-xl mx-auto">
              Join thousands of professionals using AI to create stunning headshots. No photographer, no waiting, no hassle.
            </p>
            <Button asChild size="lg" className="text-lg px-8">
              <Link to="/try-generator">Generate Your Headshot Free</Link>
            </Button>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
};

export default AIHeadshotGenerator;
